import dayjs from "dayjs";

import lodashIsEqual from "lodash/isEqual";
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";

import { Logger } from "@/common/log.utilities.js";

const logger = new Logger({
  modulePrefix: "USER SERVICE",
  file: "user.service.js",
});

const EMAIL_IGNORE_LIST = ["@geospark.io", "@seerist.com"];

const DEFAULT_LANDING_PAGE = "home";

import { SAVE_PREFS_INTERVAL_MINUTES } from "@/common/constants/home";
import {
  ENDPOINT_USER_PROFILE,
  ENDPOINT_USER_PREFERENCES,
  ENDPOINT_USER_GROUP_MEMBERS,
  ENDPOINT_USER_GROUPS,
  ENDPOINT_USER_ACCEPT_WELCOME,
  ENDPOINT_USER_MFA,
} from "@/common/constants/api";
import {
  getUserProfile,
  setUserProfile,
  getUserGroups,
} from "@/common/storage";
import { httpGet, httpPost, httpPatch } from "@/services/api.service";

export { _getProfile as getProfile };
export { _getPreferences as getPreferences };
export { _savePreferences as savePreferences };
export { _setupSavePreferences as setupSavePreferences };
export { _getGroupMembers as getGroupMembers };
export { _getGroups as getGroups };
export { _hasGroupByName as hasGroupByName };
export { _hasGroupById as hasGroupById };
export { _isAdmin as isAdmin };
export { _hasTwitterAccess as hasTwitterAccess };
export { _hasFirebaseAccess as hasFirebaseAccess };
export { _hasWatchPlusAccess as hasWatchPlusAccess };
export { _hasWatchOnlyAccess as hasWatchOnlyAccess };
export { _hasSearchOnlyAccess as hasSearchOnlyAccess };
export { _hasSourceLinkPopupAccess as hasSourceLinkPopupAccess };
export { _hasArticleHideImageAccess as hasArticleHideImageAccess };
export { _hasMassCommAdminAccess as hasMassCommAdminAccess };
export { _hasPlusCyberAccess as hasPlusCyberAccess };
export { _hasEssentialsAccess as hasEssentialsAccess };
export { _hasFiriAppAccess as hasFiriAppAccess };
export { _isInternalUser as isInternalUser };
export { _isInIgnoreUserList as isInIgnoreUserList };
export { _isWhiteLabelUser as isWhiteLabelUser };
export { _acceptWelcomeMessage as acceptWelcomeMessage };
export { _saveMFASettings as saveMFASettings };
export { _getPreferredLandingPage as getPreferredLandingPage };

let lastAuth0CallDate = dayjs().subtract(1, "day");

const _getProfile = async () => {
  try {
    return await httpGet({ url: ENDPOINT_USER_PROFILE });
  } catch (error) {
    console.error("Unable to retrieve user profile", error);
    return false;
  }
};

const _getPreferences = () => {
  let userProfile = getUserProfile();
  if (userProfile) {
    _setDefaultProfileProperties(userProfile);
    return JSON.parse(JSON.stringify(userProfile.user_metadata.preferences.v2));
  }
  return false;
};

const _getPreferredLandingPage = () => {
  let userProfile = getUserProfile();
  if (
    userProfile &&
    userProfile.user_metadata?.preferences?.v2?.home?.landingPage &&
    typeof userProfile.user_metadata.preferences.v2.home.landingPage ===
      "string"
  ) {
    return userProfile.user_metadata.preferences.v2.home.landingPage;
  }
  return DEFAULT_LANDING_PAGE;
};

const _setupSavePreferences = (preferences) => {
  const CURRENT_PREFS = _getPreferences();
  let userProfile = getUserProfile();
  if (userProfile) {
    _setDefaultProfileProperties(userProfile);
    _cleanUpV1Preferences(userProfile);

    if (
      CURRENT_PREFS &&
      preferences &&
      !lodashIsEqual(CURRENT_PREFS.v2, preferences)
    ) {
      _cleanUpUnusedPreferences(preferences);
      userProfile.user_metadata.preferences.v2 = JSON.parse(
        JSON.stringify(preferences)
      );
    }

    setUserProfile(JSON.stringify(userProfile));
    return userProfile.user_metadata.preferences;
  }
  return null;
};

const _savePreferences = async (preferences, force) => {
  const PREFS = _setupSavePreferences(preferences);
  if (PREFS) {
    const DIFF_MINUTES = dayjs().diff(lastAuth0CallDate, "minutes");
    if (force || DIFF_MINUTES > SAVE_PREFS_INTERVAL_MINUTES) {
      lastAuth0CallDate = dayjs();
      return await httpPost({
        url: ENDPOINT_USER_PREFERENCES,
        data: {
          preferences: PREFS,
        },
      });
    }
  }
  return Promise.resolve(false);
};

const _setDefaultProfileProperties = (userProfile) => {
  userProfile.user_metadata = userProfile.user_metadata || {};
  userProfile.user_metadata.preferences =
    userProfile.user_metadata.preferences || {};
  userProfile.user_metadata.preferences.v2 =
    userProfile.user_metadata.preferences.v2 || {};
  userProfile.user_metadata.preferences.v2.updated = new Date();
};

const _cleanUpUnusedPreferences = (preferences) => {
  // V2 stuff that's not used anymore
  if (
    lodashGet(preferences, "home") &&
    lodashHas(preferences.home, "infoViewer")
  ) {
    delete preferences.home["infoViewer"];
  }
  if (
    lodashGet(preferences, "home") &&
    lodashHas(preferences.home, "liveNews")
  ) {
    delete preferences.home["liveNews"];
  }
  if (lodashGet(preferences, "places")) {
    preferences.places.forEach((place) => {
      if (place["data"]) {
        delete place["data"];
      }
      if (place["color"]) {
        delete place["color"];
      }
    });
  }
  if (
    lodashGet(preferences, "alerts.content") &&
    lodashHas(preferences.alerts.content, "alertFilter") &&
    Array.isArray(preferences.alerts.content.alertFilter)
  ) {
    let cleanAlerts = [];
    preferences.alerts.content.alertFilter.forEach((alert) => {
      cleanAlerts.push({
        id: alert.id,
        name: alert.name,
      });
    });
    preferences.alerts.content.alertFilter = cleanAlerts;
  }
};

const _cleanUpV1Preferences = (userProfile) => {
  // V1 stuff
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "alertedContent")
  ) {
    delete userProfile.user_metadata.preferences["alertedContent"];
  }
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "dashboards")
  ) {
    delete userProfile.user_metadata.preferences["dashboards"];
  }
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "hyperReport")
  ) {
    delete userProfile.user_metadata.preferences["hyperReport"];
  }
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "language")
  ) {
    delete userProfile.user_metadata.preferences["language"];
  }
  if (
    lodashGet(userProfile, "user_metadata.preferences") &&
    lodashHas(userProfile.user_metadata.preferences, "tabs")
  ) {
    delete userProfile.user_metadata.preferences["tabs"];
  }
};

const _getGroupMembers = async () => {
  try {
    return await httpGet({ url: ENDPOINT_USER_GROUP_MEMBERS });
  } catch (error) {
    logger.error("FAILED TO GET USER MEMBERS");
    return false;
  }
};

/**
 * @overview Get a user's groups from auth0
 */
const _getGroups = async () => {
  try {
    const groups = await httpGet({
      url: `${ENDPOINT_USER_GROUPS}`,
      data: {},
      noCache: true,
    });
    return JSON.parse(groups).data.data;
  } catch (error) {
    logger.error("FAILED TO GET USER GROUPS");
    return null;
  }
};

/**
 * @overview Check whether a user is part of a given group or not
 */
const _hasGroupByName = (groupName) => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter(
      (el) => el.name.toLowerCase() === groupName.toLowerCase()
    );
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasGroupById = (groupId) => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.id === groupId);
  }
  return groupCheck && groupCheck.length > 0;
};

const _isAdmin = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name.toUpperCase() === "ADMIN");
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasTwitterAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "Twitter_Access");
  }
  return _isAdmin() || (groupCheck && groupCheck.length > 0);
};

const _hasFirebaseAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "Firebase_Disable");
  }
  return _isAdmin() || groupCheck.length === 0;
};

const _hasWatchPlusAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "WatchPlus_Access");
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasWatchOnlyAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "WatchOnly_Access");
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasSearchOnlyAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "SearchOnly_Access");
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasSourceLinkPopupAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter(
      (el) => el.name === "Source_Link_Popup_Access"
    );
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasArticleHideImageAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter(
      (el) => el.name === "ArticleHideImage_Access"
    );
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasMassCommAdminAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "MassCommsAdmin_Access");
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasPlusCyberAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "Seerist_Cyber_Access");
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasEssentialsAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter(
      (el) => el.name === "Seerist_Essentials_Access"
    );
  }
  return groupCheck && groupCheck.length > 0;
};

const _hasFiriAppAccess = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "FIRI_Access");
  }
  return groupCheck && groupCheck.length > 0;
};

const _isInternalUser = () => {
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter((el) => el.name === "User_Geospark");
  }
  return groupCheck && groupCheck.length > 0;
};

const _isInIgnoreUserList = () => {
  const USER = getUserProfile();
  let exists = false;
  if (USER && USER.email) {
    EMAIL_IGNORE_LIST.forEach((item) => {
      if (USER.email.indexOf(item) > -1) {
        exists = true;
      }
    });
  }
  return exists;
};

const _isWhiteLabelUser = () => {
  const WHITE_LABEL_GROUPS = ["User_Brosnan_Risk_Consultants"];
  const userGroups = getUserGroups();
  let groupCheck;
  if (userGroups) {
    groupCheck = userGroups.filter(
      (el) => WHITE_LABEL_GROUPS.indexOf(el.name) > -1
    );
  }
  return groupCheck && groupCheck.length > 0;
};

const _acceptWelcomeMessage = async () => {
  try {
    let result = await httpPatch({
      url: ENDPOINT_USER_ACCEPT_WELCOME,
      data: {},
    });
    result = JSON.parse(result);
    return true;
  } catch (err) {
    console.error("Unable to accept welcome message", error);
    return false;
  }
};

const _saveMFASettings = async (setting) => {
  try {
    let result = await httpPatch({
      url: `${ENDPOINT_USER_MFA}/?mfaSetting=${setting}`,
      data: {},
    });
    result = JSON.parse(result);
    return true;
  } catch (err) {
    console.error("Unable to save MFA settings", error);
    return false;
  }
};
